import React, { useState, useEffect } from 'react';
import { Layout, Menu, Tag, Button, Dropdown, Space, Modal, Form, Input, message } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  DashboardOutlined, UserOutlined, LogoutOutlined, KeyOutlined,
  LaptopOutlined, StockOutlined, DesktopOutlined, TeamOutlined,
  BarsOutlined, UnorderedListOutlined, SafetyOutlined, RobotOutlined
} from '@ant-design/icons';
import { systemApi, authApi, menuApi } from '../services/api';
import { useAuth } from '../utils/auth';
import NotFoundPage from '../pages/NotFoundPage';

const { Header, Sider, Content } = Layout;

const iconMap = {
  DashboardOutlined: <DashboardOutlined />,
  StockOutlined: <StockOutlined />,
  UnorderedListOutlined: <UnorderedListOutlined />,
  DesktopOutlined: <DesktopOutlined />,
  LaptopOutlined: <LaptopOutlined />,
  TeamOutlined: <TeamOutlined />,
  BarsOutlined: <BarsOutlined />,
  UserOutlined: <UserOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  RobotOutlined: <RobotOutlined />,
};

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [sysStatus, setSysStatus] = useState(null);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const [pwdLoading, setPwdLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [forbidden, setForbidden] = useState(false);

  // 系统状态轮询
  useEffect(() => {
    systemApi.getStatus().then(r => { if (r.success) setSysStatus(r.data); }).catch(() => {});
    const timer = setInterval(() => {
      systemApi.getStatus().then(r => { if (r.success) setSysStatus(r.data); }).catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  // 加载动态菜单（树形：支持父子子菜单）
  useEffect(() => {
    if (!user) return;
    menuApi.getMenus().then(r => {
      if (r.success && Array.isArray(r.data)) {
        const build = (menus) => (menus || []).map(m => {
          const item = {
            key: m.key,
            icon: m.children && m.children.length ? null : (iconMap[m.icon] || <DesktopOutlined />),
            label: m.label,
          };
          if (m.children && m.children.length) {
            item.children = build(m.children);
          }
          return item;
        });
        // 菜单完全由数据库 ths_sys_menus 驱动（含父子菜单），不再前端硬编码
        setMenuItems(build(r.data));
      }
    }).catch(() => {});
  }, [user]);

  // 路由守卫：当前页面不在菜单权限中则显示404
  useEffect(() => {
    if (menuItems.length === 0) return;
    const collectKeys = (items) => {
      let keys = [];
      items.forEach(m => {
        keys.push(m.key);
        if (m.children) keys = keys.concat(collectKeys(m.children));
      });
      return keys;
    };
    const permittedKeys = new Set(collectKeys(menuItems));
    const currentKey = location.pathname.split('/')[1] || 'dashboard';
    setForbidden(!permittedKeys.has(currentKey));
  }, [location.pathname, menuItems]);

  // 未登录跳转
  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  if (loading) return null;
  if (!user) return null;

  const onMenuClick = ({ key }) => navigate('/' + key);

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: `用户: ${user?.nickname || user?.username || '未知'}` },
      { type: 'divider' },
      { key: 'changePwd', icon: <KeyOutlined />, label: '修改密码' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') { logout(); navigate('/login'); }
      else if (key === 'changePwd') setPwdModalOpen(true);
    },
  };

  const handleChangePwd = async () => {
    try {
      const values = await pwdForm.validateFields();
      if (values.newPassword !== values.confirmPassword) { message.error('两次输入的新密码不一致'); return; }
      setPwdLoading(true);
      const res = await authApi.changePassword(values.oldPassword, values.newPassword);
      if (res.success) {
        message.success('密码修改成功，请重新登录');
        setPwdModalOpen(false);
        pwdForm.resetFields();
        logout();
        navigate('/login');
      } else message.error(res.message || '修改失败');
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.response?.data?.message || '请求失败');
    } finally { setPwdLoading(false); }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} theme="dark" width={200}
        style={{ borderRight: '1px solid #1a1a2e' }}>
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: collapsed ? 14 : 16, fontWeight: 'bold',
          borderBottom: '1px solid #1a1a2e', background: 'linear-gradient(135deg, #c0392b, #e74c3c)' }}>
          {!collapsed && '股小T股票智能交易系统'}
        </div>
        <Menu theme="dark" mode="inline"
          selectedKeys={[location.pathname.split('/')[1] || 'dashboard']}
          items={menuItems} onClick={onMenuClick} style={{ borderRight: 0 }} />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', height: 48 }}>
          <Space>
            {sysStatus && (
              <Tag color={sysStatus.server === 'running' ? 'green' : 'red'}>
                {sysStatus.server === 'running' ? '运行中' : '异常'}
              </Tag>
            )}
          </Space>
          <Dropdown menu={userMenu} placement="bottomRight">
            <Button type="text" icon={<UserOutlined />}>{user?.nickname || user?.username}</Button>
          </Dropdown>
        </Header>
        <Content style={{ margin: 16, minHeight: 280 }}>{forbidden ? <NotFoundPage /> : <Outlet />}</Content>
      </Layout>

      <Modal title={`修改密码 - ${user?.username || ''}`} open={pwdModalOpen} onOk={handleChangePwd}
        onCancel={() => { setPwdModalOpen(false); pwdForm.resetFields(); }}
        confirmLoading={pwdLoading} okText="确认修改" cancelText="取消" destroyOnClose>
        <Form form={pwdForm} layout="vertical" autoComplete="off">
          <Form.Item name="oldPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="输入当前密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少6位' }]}>
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[
            { required: true, message: '请再次输入新密码' },
            ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('newPassword') === value) return Promise.resolve(); return Promise.reject(new Error('两次输入的密码不一致')); } }),
          ]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
